import React, { FC, useEffect } from 'react';

import { V1CloudEventsPathSuggestion } from '@ariksa/audittrail/api';
import { Box, Center, HStack, Stack, Text } from '@chakra-ui/react';
import { filter, isEqual } from 'lodash';
import { customTheme } from 'theme';

import { ActionButton, PrimaryButton, Select } from 'components/DataEntry';
import {
  FileIcon,
  getIcon,
  getIconColor,
  IconTypes,
  TrashIcon,
} from 'components/index';

import {
  flattenPaths,
  getFolderOptionsWithSelection,
  getSelectedFolderCount,
  normalizeSelectedFolders,
  SelectOption,
} from '../utils';

export {
  flattenPaths,
  flattenSelectedPaths,
  getFolderOptionsWithSelection,
  getSelectedFolderCount,
  getSelectedFolderPaths,
  normalizeSelectedFolders,
} from '../utils';

export interface ActivityResourceRow {
  id: string;
  resource?: SelectOption;
  folders: SelectOption[];
}

interface Props {
  resources: ActivityResourceRow[];
  resourceOptions: SelectOption[];
  objectPathsByBucket: Record<string, V1CloudEventsPathSuggestion[]>;
  isObjectPathsLoading: boolean;
  onAddResource: () => void;
  onResourceChange: (resource?: SelectOption | null) => void;
  onUpdateRow: (rowId: string, update: Partial<ActivityResourceRow>) => void;
  onRemoveRow: (rowId: string) => void;
}

export const ActivityGroupFormResource: FC<Props> = ({
  resources,
  resourceOptions,
  objectPathsByBucket,
  isObjectPathsLoading,
  onAddResource,
  onResourceChange,
  onUpdateRow,
  onRemoveRow,
}) => {
  const getFolderOptions = (row: ActivityResourceRow) => {
    const paths = objectPathsByBucket[row.resource?.bucketName ?? ''];
    const options = flattenPaths(paths ?? []);
    const existing = row.folders.filter(
      folder => !options.some(option => option.value === folder.value),
    );

    return getFolderOptionsWithSelection(
      [...options, ...existing],
      row.folders,
    );
  };

  useEffect(() => {
    resources.forEach(row => {
      if (!row.resource || !row.folders.length) return;

      const paths = objectPathsByBucket[row.resource.bucketName ?? ''];
      if (!paths?.length) return;

      const normalizedFolders = normalizeSelectedFolders(
        row.folders,
        flattenPaths(paths),
      );
      const hasNewFolders =
        normalizedFolders.length !== row.folders.length ||
        normalizedFolders.some((folder, index) => {
          const currentFolder = row.folders[index];

          return (
            folder.value !== currentFolder?.value ||
            folder.parentValue !== currentFolder?.parentValue ||
            !isEqual(folder.descendantValues, currentFolder?.descendantValues)
          );
        });

      if (hasNewFolders) {
        onUpdateRow(row.id, { folders: normalizedFolders });
      }
    });
  }, [objectPathsByBucket, onUpdateRow, resources]);

  //this is to filter already selected resources
  const getResourceOptions = (rowId: string) => {
    const selected = new Set(
      resources
        .filter(row => row.id !== rowId)
        .map(row => row.resource?.value)
        .filter(Boolean),
    );

    return filter(resourceOptions, option => !selected.has(option.value));
  };

  const handleFolderChange = (
    row: ActivityResourceRow,
    folders: SelectOption[] | null,
    event?: Record<string, any>,
  ) => {
    const selectedFolders = folders ?? [];
    const changedFolder = event?.option ?? event?.removedValue;
    const selectedValues = new Set(selectedFolders.map(folder => folder.value));

    if (
      changedFolder?.descendantValues?.length &&
      event?.action === 'select-option'
    ) {
      changedFolder.descendantValues.forEach(value =>
        selectedValues.add(value),
      );
    }

    if (
      changedFolder?.descendantValues?.length &&
      (event?.action === 'deselect-option' || event?.action === 'remove-value')
    ) {
      changedFolder.descendantValues.forEach(value =>
        selectedValues.delete(value),
      );
    }

    const folderOptions = getFolderOptions(row);
    const optionsByValue = new Map(
      [...selectedFolders, ...folderOptions].map(option => [
        option.value,
        option,
      ]),
    );

    // A parent represents all of its descendants. Remove it when any child
    // is deselected, but keep the remaining sibling selections intact.
    folderOptions.forEach(option => {
      if (
        selectedValues.has(option.value) &&
        option.descendantValues?.length &&
        !option.descendantValues.every(value => selectedValues.has(value))
      ) {
        selectedValues.delete(option.value);
      }
    });

    const normalizedFolders = [...selectedValues]
      .map(value => optionsByValue.get(value))
      .filter(Boolean) as SelectOption[];

    onUpdateRow(row.id, { folders: normalizedFolders });
  };

  return (
    <Stack
      border="1px solid"
      borderColor="componentBorder"
      borderRadius={6}
      spacing={3}
      py={4}
    >
      <HStack justify="space-between" w="full" minW={0} px={4}>
        <Text color={customTheme.colors.gray['300']}>
          Choose buckets and folders to track activity
        </Text>
        <PrimaryButton
          ml="auto"
          border="1px solid"
          borderColor="gray.200"
          color="primary"
          bg="white"
          px={3}
          py={1}
          height="auto"
          onClick={onAddResource}
        >
          + ADD RESOURCE
        </PrimaryButton>
      </HStack>
      <Box borderTop="1px solid" borderColor="componentBorder" />
      <HStack pr={5} pt={4}>
        <Text pl={10} w="50%" fontWeight={600}>
          Resources
        </Text>
        <Text pl={2} w="50%" fontWeight={600}>
          Folders
        </Text>
      </HStack>
      <Stack spacing={3} px={5} pb={4}>
        {resources.map(row => (
          <HStack key={row.id} align="flex-start">
            <Box w="50%">
              <Select
                options={getResourceOptions(row.id)}
                value={row.resource}
                onChange={resource => {
                  onUpdateRow(row.id, { resource, folders: [] });
                  onResourceChange(resource);
                }}
                placeholder="Select bucket"
                showClearIndicator={false}
                showIconInValueContainer
                valueContainerIcon={
                  <Center color="orange.300">
                    {getIcon(IconTypes.SimpleStorageService)}
                  </Center>
                }
              />
            </Box>
            <Box w="50%">
              <Select
                options={getFolderOptions(row)}
                value={row.folders}
                onChange={(folders, event) =>
                  handleFolderChange(row, folders, event)
                }
                placeholder="Select folders"
                isMulti
                closeMenuOnSelect={false}
                showTotalSelected
                showGroupCollapse
                selectedCount={getSelectedFolderCount(row.folders)}
                selectedMessage={
                  getSelectedFolderCount(row.folders) === 1
                    ? 'folder'
                    : 'folders'
                }
                showClearIndicator
                showIconInValueContainer
                isDisabled={!row.resource}
                isLoading={isObjectPathsLoading}
                valueContainerIcon={
                  <Center boxSize={5} color={getIconColor(IconTypes.S3Folder)}>
                    <FileIcon />
                  </Center>
                }
                onClickGroupHeading={r => console.log(r)}
                styles={{
                  option: (provided, option) => {
                    const data = option.data as SelectOption;
                    return {
                      ...provided,
                      paddingLeft: `${12 + (data.depth ?? 0) * 24}px`,
                      backgroundColor: 'transparent',
                      '&:hover': {
                        backgroundColor: 'transparent',
                      },
                      '&:active': {
                        backgroundColor: 'transparent',
                      },
                    };
                  },
                }}
              />
            </Box>
            <ActionButton
              icon={<TrashIcon color="delete" />}
              label="Remove"
              onClick={() => onRemoveRow(row.id)}
            />
          </HStack>
        ))}
      </Stack>
    </Stack>
  );
};
